import Box from '@mui/system/Box';
import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material';
import { useResponsive } from 'src/hooks/use-responsive';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { formatNumber } from 'src/utils/format-number';

type Props = {
    series: number[];
    labels: string[];
    breakdownDimension: string;
};

const capitalizeFirst = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

// Palette country (ordre spécifique)
const countryPalette = [
    '#003768',
    '#006C9C',
    '#66a4d1',
    '#a5d8fe',
    '#CAFDF5',
    '#D3FCD2',
    '#77ED8B',
    '#22C55E',
    '#118D57',
    '#065E49',
];

// Détermine si on doit arrondir (channel ou conversions)
const shouldRound = (label: string, breakdownDimension: string, metricsName?: string) => {
    const normalized = label.trim().toLowerCase();
    if (breakdownDimension === 'channel') return true;
    if (metricsName && String(metricsName).trim().toLowerCase() === 'conversions') return true;
    return ['paid', 'organic', 'total', 'brand'].includes(normalized);
};

export default function ChartDonutConversions({ series, labels, breakdownDimension }: Props) {
    const theme = useTheme();
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const numericSeriesRaw = (series || []).map((item) => Number(item));

    // Normalisation des labels pour platform/device
    let displayLabels = labels || [];
    if (breakdownDimension === 'platform' || breakdownDimension === 'device') {
        displayLabels = labels.map(capitalizeFirst);
    }

    // Total affiché (arrondi si nécessaire)
    const totalRaw = numericSeriesRaw.reduce((acc, val) => acc + val, 0);
    const totalDisplay = shouldRound('total', breakdownDimension) ? Math.round(totalRaw) : totalRaw;

    // Prépare palette de couleurs
    let colors: string[] = [];
    const channelColorsMap: Record<string, string> = {
        Paid: "rgba(0, 108, 156, 1)",
        Organic: "rgba(17, 141, 87, 1)",
        Revenue: theme.palette.revenue?.dark || '#006C9C',
    };
    const platformColorMap: Record<string, string> = {
        google: '#66a4d1',
        bing: '#006C9C',
    };
    const devicesColorMap = ['#006C9C', theme.palette.paid?.dark || '#1E88E5', theme.palette.organic?.dark || '#22C55E'];

    if (breakdownDimension === 'channel') {
        colors = displayLabels.map((label) => channelColorsMap[label] || theme.palette.grey[400]);
    } else if (breakdownDimension === 'platform') {
        colors = displayLabels.map((label) => {
            const key = (label || '').toLowerCase();
            return platformColorMap[key] || '#1E88E5';
        });
    } else if (breakdownDimension === 'device') {
        colors = devicesColorMap.slice(0, displayLabels.length);
    } else if (breakdownDimension === 'country') {
        // attribution stable par ordre d'apparition
        const map: Record<string, string> = {};
        let idx = 0;
        displayLabels.forEach((lbl) => {
            const key = lbl.trim().toLowerCase();
            if (!(key in map)) {
                map[key] = countryPalette[idx % countryPalette.length];
                idx += 1;
            }
        });
        colors = displayLabels.map((lbl) => map[lbl.trim().toLowerCase()] || '#1E88E5');
    } else {
        const fallback = ['#1E88E5', '#64B5F6', '#B7DCFB', '#006C9C'];
        colors = displayLabels.map((_, i) => fallback[i % fallback.length]);
    }

    const lgUp = useResponsive('up', 'lg');

    const chartOptions = useChart({
        colors,
        labels: displayLabels,
        stroke: {
            show: false,
        },

        legend: {
            horizontalAlign: 'center',
            position: 'bottom',
            fontSize: '12px',
        },
        tooltip: {
            shared: true,

            fillSeriesColor: false,
            custom: function ({ series: s, seriesIndex, w }: any) {
                const label = displayLabels[seriesIndex] || w.globals.labels[seriesIndex];
                const rawValue = s[seriesIndex];
                const value = shouldRound(label, breakdownDimension) ? Math.round(rawValue) : rawValue;
                const total = s.reduce((a: any, b: any) => a + b, 0);
                const percentage = total ? ((rawValue / total) * 100).toFixed(2) : '0.00';
                const color = w.globals.colors[seriesIndex] || '#181C32';

                return `
                    <div style="background: #fff;
                                color: #181C32;
                                padding: 4px 7px;
                                border-radius: 8px;
                                box-shadow: 0 3px 16px 0 rgba(0,0,0,.24);
                                font-size:12px;
                                font-weight:600;">
                        <span style="color:${color}; font-weight: 900">${label}:</span> 
                        <span style="margin-left:4px;">${formatNumber(value)} (${Number(percentage)?.toFixed(0)}%)</span>
                    </div>
                `;
            },
        },

        responsive: [
            {
                breakpoint: 0,
                options: {
                    chart: {
                        width: '600px',
                    },
                },
            },
            {
                breakpoint: 480,
                options: {
                    chart: {
                        width: '450px',
                    },
                },
            },
        ],
    });

    return (
        <Box sx={{ mt: 8, ml: 10 }}>
            <Chart dir="ltr" type="donut" series={numericSeriesRaw} options={chartOptions} width={400} />
        </Box>
    );
}
